import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import Mention from '@tiptap/extension-mention';
import {
  clearFormatting,
  getMentionedHandles,
  queryUsers,
} from '../utils/editorHelpers';

/**
 * Wires up the TipTap editor with StarterKit + Link + CharacterCount + a
 * custom Mention suggestion pipeline.
 *
 * All React state the extensions need to flip (mention dropdown props,
 * placeholder visibility, mobile toolbar visibility, focus/blur, etc.) is
 * passed in by the parent as setters + refs so this hook stays a
 * straight-line configuration module.
 */
export default function useTipTapEditor({
  initialContent,
  placeholderText,
  maxLength,
  isMobile,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  mentionActiveRef,
  mentionDismissReasonRef,
  mentionListRef,
  setMentionProps,
  setShowPlaceholderOverlay,
  setEditorFocused,
  setMobileToolbarVisible,
  blurTimerRef,
}) {
  return useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // StarterKit bundles Link; we register Link.configure() below — keep one only
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      CharacterCount.configure({ limit: maxLength }),
      Mention.configure({
        renderHTML({ node }) {
          const handle = node.attrs.label ?? node.attrs.id;
          return [
            'span',
            {
              'data-type': 'mention',
              'data-id': node.attrs.id,
              'data-label': handle,
              'data-mention-handle': node.attrs.id,
            },
            `@${handle}`,
          ];
        },
        parseHTML() {
          return [{ tag: 'span[data-type="mention"]' }];
        },
        suggestion: {
          items: async ({ query }) => queryUsers(query),
          render: () => ({
            onStart: (props) => {
              mentionDismissReasonRef.current = null;
              mentionActiveRef.current = true;
              setMentionProps({ ...props });
            },
            onUpdate: (props) => {
              mentionActiveRef.current = true;
              setMentionProps({ ...props });
            },
            onKeyDown: ({ event }) => {
              if (event.key === ' ' || event.key === 'Spacebar') {
                mentionDismissReasonRef.current = 'space';
              }
              return (
                mentionListRef.current?.handleKeyDown({ event }) ?? false
              );
            },
            onExit: (props) => {
              mentionActiveRef.current = false;
              setMentionProps(null);
              if (
                mentionDismissReasonRef.current === 'space' &&
                props?.editor
              ) {
                clearFormatting(props.editor);
              }
              mentionDismissReasonRef.current = null;
            },
          }),
        },
      }),
    ],
    content: initialContent || '',
    editorProps: {
      attributes: {
        'aria-placeholder': placeholderText,
        'aria-multiline': 'true',
      },
      handleKeyDown: (view, event) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          event.shiftKey &&
          (event.key === 'm' || event.key === 'M')
        ) {
          event.preventDefault();
          const ed = view.dom?.editor;
          if (ed) clearFormatting(ed);
          return true;
        }
        if (mentionActiveRef.current) return false;
        if (onKeyDown) return onKeyDown(event) ?? false;
        return false;
      },
    },
    onCreate: ({ editor: ed }) => {
      const chars = ed.storage.characterCount.characters();
      setShowPlaceholderOverlay(chars === 0);
    },
    onUpdate: ({ editor }) => {
      const chars = editor.storage.characterCount.characters();
      setShowPlaceholderOverlay(chars === 0);
      onChange?.(editor.getHTML(), chars, getMentionedHandles(editor));
    },
    onFocus: ({ event }) => {
      clearTimeout(blurTimerRef.current);
      setEditorFocused(true);
      if (isMobile) setMobileToolbarVisible(true);
      onFocus?.(event);
    },
    onBlur: ({ event }) => {
      blurTimerRef.current = setTimeout(
        () => setMobileToolbarVisible(false),
        150
      );
      setEditorFocused(false);
      onBlur?.(event);
    },
  });
}
